import React, { useEffect } from "react";
import { Box, Heading, Text, Icon, HStack } from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

/**
 * RewardCelebration — shown on the public form success state when a reward
 * was awarded on submit. Fires a confetti burst on mount.
 *
 * `canvas-confetti` is dynamically imported so admin bundles that never show
 * this celebration don't pay the ~12 KB cost.
 */

interface RewardCelebrationProps {
  count: number;
  tokenName: string;
}

const RewardCelebration: React.FC<RewardCelebrationProps> = ({ count, tokenName }) => {
  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const mod = await import("canvas-confetti");
        if (disposed) return;
        const confetti = mod.default;
        const burst = (originX: number) =>
          confetti({
            particleCount: 80,
            spread: 70,
            startVelocity: 35,
            origin: { x: originX, y: 0.4 },
            colors: ["#5AC8FA", "#4FD1C5", "#F6AD55", "#F687B3", "#68D391"],
          });
        burst(0.25);
        burst(0.75);
        setTimeout(() => !disposed && burst(0.5), 250);
      } catch (err) {
        // Confetti is non-essential — silently swallow if the dynamic import fails.
        console.warn("[RewardCelebration] confetti import failed:", err);
      }
    })();
    return () => {
      disposed = true;
    };
  }, []);

  return (
    <Box
      borderRadius="lg"
      p={{ base: 4, md: 6 }}
      bg="linear-gradient(135deg, #5AC8FA22 0%, #4FD1C522 100%)"
      borderWidth="1px"
      borderColor="#5AC8FA55"
      textAlign="center"
    >
      <HStack justify="center" spacing={2} mb={2}>
        <Icon as={CheckCircleIcon} color="green.500" boxSize={6} />
        <Heading size="md">🎉 You just earned {count} {tokenName}!</Heading>
      </HStack>
      <Text fontSize="sm" color="gray.600">
        Spend it toward your next order — every {count === 1 ? tokenName : tokenName.replace(/s$/, "")}{" "}
        is a dollar off at checkout.
      </Text>
    </Box>
  );
};

export default RewardCelebration;
